import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronLeft, ChevronRight, UserPlus, Users } from 'lucide-react';
import StudentCard from '@components/portfolio/StudentCard';
import Button from '@components/common/Button';
import portfolioService from '@services/portfolio.service';
import toast from 'react-hot-toast';
import { ROUTES } from '@constants';
import { shouldRetryApiCall, resetApiRetry } from '@utils/apiRetry';

const StudentsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allStudents, setAllStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    fieldOfStudy: '',
    graduationYear: '',
  });
  const studentsPerPage = 12;
  const [fetchBlocked, setFetchBlocked] = useState(false);
  const observerTarget = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filterStudentsLocally = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.length === 0) {
      setStudents(allStudents);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allStudents.filter(student => {
      const fullName = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase().trim();
      const email = (student.email || '').toLowerCase();
      const skills = (student.skills || []).join(' ').toLowerCase();
      const fieldOfStudy = (student.fieldOfStudy || '').toLowerCase();
      const institution = (student.institution || '').toLowerCase();
      const designation = (student.currentDesignation || '').toLowerCase();
      
      return fullName.includes(query) ||
             email.includes(query) ||
             skills.includes(query) ||
             fieldOfStudy.includes(query) ||
             institution.includes(query) ||
             designation.includes(query);
    });

    setStudents(filtered);
  }, [allStudents]);

  useEffect(() => {
    setAllStudents([]);
    setStudents([]);
    setCurrentPage(1);
    setHasMore(true);
    setFetchBlocked(false);
  }, [filters]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      filterStudentsLocally(debouncedSearchTerm);
    } else {
      setStudents(allStudents);
    }
  }, [debouncedSearchTerm, allStudents, filterStudentsLocally]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && !fetchBlocked) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, fetchBlocked]);

  const fetchStudents = useCallback(async () => {
    const apiKey = 'getStudentPortfolios';

    if (fetchBlocked || !shouldRetryApiCall(apiKey)) {
      setFetchBlocked(true);
      setLoading(false);
      setLoadingMore(false);
      if (!toast.isActive('max-retries')) {
        toast.error('Maximum retry attempts reached. Please refresh the page.', {
          id: 'max-retries',
          duration: 5000
        });
      }
      return;
    }

    try {
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: currentPage,
        limit: studentsPerPage,
        ...filters,
      };

      const response = await portfolioService.getStudentPortfolios(params);

      if (response.success) {
        const responseData = response.data.data || response.data;
        const studentsData = responseData.students || [];
        const totalPagesFromAPI = responseData.pagination?.totalPages || 1;

        if (currentPage === 1) {
          setAllStudents(studentsData);
          setStudents(studentsData);
        } else {
          setAllStudents((prev) => [...prev, ...studentsData]);
          setStudents((prev) => [...prev, ...studentsData]);
        }

        setTotalStudents(responseData.pagination?.total || 0);
        setTotalPages(totalPagesFromAPI);
        setHasMore(currentPage < totalPagesFromAPI);

        resetApiRetry(apiKey);
        setFetchBlocked(false);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);

      const errorStatus = error.response?.status;

      if (errorStatus >= 500) {
        shouldRetryApiCall(apiKey, errorStatus);
        setFetchBlocked(true);
        toast.error('Server error. Please try again later.');
      } else if (errorStatus === 429) {
        shouldRetryApiCall(apiKey, errorStatus);
        toast.error('Too many requests. Please wait a moment.');
      } else if (!error.response) {
        shouldRetryApiCall(apiKey, 0);
        toast.error('Cannot connect to server. Please check your connection.');
      } else {
        shouldRetryApiCall(apiKey, errorStatus);
        toast.error('Failed to load student portfolios');
      }

      if (currentPage === 1) {
        setAllStudents([]);
        setStudents([]);
        setTotalStudents(0);
        setTotalPages(1);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currentPage, filters, fetchBlocked, studentsPerPage]);

  useEffect(() => {
    if (!fetchBlocked && hasMore) {
      fetchStudents();
    }
  }, [currentPage, filters, fetchBlocked, hasMore, fetchStudents]);

  const handleSearch = (e) => {
    e.preventDefault();
    setDebouncedSearchTerm(searchTerm.trim());
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  return (
    <div className="w-full min-h-screen flex flex-col">
      {/* Search */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, skills, or field..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {searchTerm && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setDebouncedSearchTerm('');
              }}
              className="sm:w-auto"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Student Grid */}
      <div className="flex-1">
      {loading ? (
        <div className="grid gap-4 md:gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className="relative bg-white rounded-xl shadow-sm p-5 animate-pulse"
            >
              {/* status dot placeholder */}
              <div className="absolute top-4 left-4 w-3 h-3 rounded-full bg-gray-200"></div>
              {/* match badge placeholder */}
              <div className="absolute top-4 right-4 h-5 w-20 rounded-full bg-gray-200"></div>

              <div className="flex justify-center pt-6 pb-4">
                <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
              </div>

              <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto mb-3"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/5 mx-auto mb-4"></div>

              <div className="flex justify-center gap-2 mb-4">
                <div className="h-5 w-14 bg-gray-200 rounded-full"></div>
                <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                <div className="h-5 w-12 bg-gray-200 rounded-full"></div>
              </div>

              <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
            </div>
          ))}
        </div>
      ) : students.length > 0 ? (
        <>
          <div className="grid gap-4 md:gap-5 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {students.map((student) => (
              <div key={student.id} className="h-full">
                <StudentCard student={student} />
              </div>
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={observerTarget} className="w-full py-4">
            {loadingMore && (
              <div className="flex justify-center items-center gap-2">
                <div className="w-6 h-6 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-primary-600 font-medium">Loading more students...</span>
              </div>
            )}
            {!hasMore && students.length >= studentsPerPage && (
              <div className="text-center text-gray-500">
                <p>You've reached the end of the list</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-16">
          <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-primary-700 mb-2">
            No Students Found
          </h3>
          <p className="text-primary-600 mb-4">
            Try adjusting your search or filters
          </p>
          <Button
            variant="primary"
            onClick={() => {
              setFilters({ fieldOfStudy: '', graduationYear: '' });
              setSearchTerm('');
              setDebouncedSearchTerm('');
              setAllStudents([]);
              setStudents([]);
              setCurrentPage(1);
              setHasMore(true);
              setFetchBlocked(false);
            }}
          >
            Clear All Filters
          </Button>
        </div>
      )}
      </div>
    </div>
  );
};

export default StudentsList;